import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Typography
} from '@material-ui/core'
import React from 'react'

import { getTranslate } from '../../../localization'
import useStyles from './styles/DaysOfWeekPicker.styles'

/**
 * The seven day indices, SATURDAY FIRST, in the order they are rendered.
 *
 * The order is not a display preference — it is the stored encoding. loyalty defines
 * `0=Sat 1=Sun 2=Mon 3=Tue 4=Wed 5=Thu 6=Fri` (`internal/promo/types.go`), and the
 * migration that introduced the column says why: the Iranian weekend (Thursday + Friday)
 * is then `{5,6}` — contiguous, at the end of the row, and readable as a weekend. Under
 * Postgres's own 0=Sunday it would be `{4,5}`, sitting mid-week.
 *
 * Rendering Sunday-first while writing this encoding is the exact defect that shipped in
 * the demo data: the seeder stored `{4,5}` under a comment claiming "Thursday and Friday",
 * so a promotion named "Weekend stays" could never fire on a Friday. The index and the
 * label must come from one place, which is this array.
 */
const DAYS: Array<{ index: number; label: string }> = [
  { index: 0, label: 'Saturday' },
  { index: 1, label: 'Sunday' },
  { index: 2, label: 'Monday' },
  { index: 3, label: 'Tuesday' },
  { index: 4, label: 'Wednesday' },
  { index: 5, label: 'Thursday' },
  { index: 6, label: 'Friday' }
]

interface DaysOfWeekPickerProps {
  value: number[]
  onChange: (days: number[]) => void
  disabled?: boolean
}

/**
 * Real checkboxes in a labelled group, not toggle chips.
 *
 * `FormControlLabel` is the portal's field control (52 screens) and `ToggleButton` has zero
 * call sites, so chips would be a new interaction pattern here. They would also be a worse
 * one: a checkbox group is a single tab stop per option with native checked state, where a
 * clickable chip has to reimplement keyboard access and announce its own selectedness.
 */
const DaysOfWeekPicker: React.FC<DaysOfWeekPickerProps> = ({
  value,
  onChange,
  disabled
}) => {
  const classes = useStyles()

  const toggle = (index: number) =>
    onChange(
      value.includes(index)
        ? value.filter((day) => day !== index)
        : // Kept sorted so the stored array does not depend on the order the operator
          // happened to click, which would otherwise make two identical schedules compare
          // as different.
          [...value, index].sort((a, b) => a - b)
    )

  return (
    <FormControl
      component="fieldset"
      className={classes.root}
      disabled={disabled}>
      <FormLabel component="legend" className={classes.legend}>
        {getTranslate('Days of week')}
      </FormLabel>
      <FormGroup row className={classes.group}>
        {DAYS.map((day) => (
          <FormControlLabel
            key={day.index}
            className={classes.day}
            control={
              <Checkbox
                size="small"
                color="primary"
                checked={value.includes(day.index)}
                onChange={() => toggle(day.index)}
                inputProps={{ 'aria-label': getTranslate(day.label) }}
              />
            }
            label={getTranslate(day.label)}
          />
        ))}
      </FormGroup>
      <Typography variant="caption" className={classes.helper}>
        {getTranslate('Leave every day unchecked to apply on all days.')}
      </Typography>
    </FormControl>
  )
}

export default DaysOfWeekPicker
export { DAYS }
